import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    legal: [
      { path: '/politique-confidentialite', label: 'Politique de Confidentialité' },
      { path: '/politique-cookies', label: 'Politique des Cookies' },
      { path: '/conditions-generales', label: 'Conditions Générales' },
    ],
    company: [
      { path: '/apropos', label: 'À Propos' },
      { path: '/faq', label: 'FAQ' },
      { path: '/comparaison', label: 'Comparaison des Offres' },
      { path: '/contact', label: 'Contact' },
    ],
  };

  return (
    <footer className="bg-dark-surface border-t border-dark-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <img
                src="/images/logo.webp"
                alt="Primorixx"
                className="w-auto"
                loading="lazy"
                style={{ maxHeight: '50px', height: '50px', width: 'auto' }}
              />
              <span className="text-xl font-bold gradient-text">Primorixx</span>
            </Link>
            <p className="text-dark-muted text-sm mb-4">
              Les meilleures analyses d'offres de sites de paris sportifs en France.
              Trouvez les bonus les plus généreux et les bookmakers les plus fiables.
            </p>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-dark-muted hover:text-dark-accent transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Entreprise</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-dark-muted hover:text-dark-accent transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Responsible Gaming */}
          <div>
            <h3 className="text-white font-semibold mb-4">Jeu Responsable</h3>
            <p className="text-dark-muted text-sm mb-4">
              Le jeu peut être addictif. Jouez de manière responsable. Si vous avez
              besoin d'aide, contactez-nous.
            </p>
            <Link
              to="/jeu-responsable"
              className="text-dark-accent hover:text-dark-accentHover font-medium text-sm inline-block mb-4"
            >
              En Savoir Plus sur le Jeu Responsable →
            </Link>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://anj.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/anj.webp"
                  alt="ANJ"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <a
                href="https://www.hippodromesenfete.fr/jouons-responsable/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/jouns.webp"
                  alt="Jouons Responsable"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <a
                href="https://www.data.gouv.fr/en/organizations/autorite-de-regulation-des-jeux-en-ligne-arjel/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/arjel.webp"
                  alt="ARJEL"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <a
                href="https://www.gambleaware.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/4rad.webp"
                  alt="GambleAware"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <a
                href="https://mediateurdesjeuxenligne.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/mediater.webp"
                  alt="Médiateur des Jeux en Ligne"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <a
                href="https://www.gambleaware.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/images/footer/begamble.webp"
                  alt="BeGambleAware"
                  className="footer-icon-small"
                  loading="lazy"
                  style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
                />
              </a>
              <img
                src="/images/footer/+18.webp"
                alt="18+"
                className="opacity-70 footer-icon-small"
                loading="lazy"
                style={{ maxHeight: '32px', height: '32px', width: 'auto', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-dark-border mt-8 pt-8">
          {/* 18+ Disclaimer */}
          <div className="bg-dark-card border border-dark-border rounded-lg p-4 mb-6 text-center">
            <p className="text-white font-semibold text-base mb-2">
              <span className="text-red-400 font-bold text-lg">18+</span> - Jeu Interdit aux Mineurs
            </p>
            <p className="text-dark-muted text-sm">
              Les jeux d'argent et de hasard peuvent être dangereux: pertes d'argent, conflits familiaux, addiction. 
              Retrouvez nos conseils sur{' '}
              <a 
                href="https://www.joueurs-info-service.fr/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-dark-accent hover:underline"
              >
                joueurs-info-service.fr
              </a>
              {' '}(09 74 75 13 13 - Appel non surtaxé)
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-dark-muted text-sm">
              © {currentYear} Primorixx. Tous droits réservés. | Jouez
              de manière responsable. 18+
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

